import { useCallback, useEffect, useRef } from 'react';

import {
  escapeForCssAttributeSelector,
  findDropdownElement,
} from '../utils/dataProviderUtils';

interface UseInfiniteScrollProps {
  /**
   * The data-testid of the select component wrapper.
   * Used to find the dropdown `<ul>` in the DOM.
   */
  dataTestId: string;
  /** Whether more pages are available from the server */
  hasMoreOptions: boolean;
  /** Whether the initial/search load (page 0) is in progress */
  isLoading: boolean;
  /** Whether a pagination load (page 1+) is in progress */
  isLoadingMore: boolean;
  /** Current number of options in state (drives re-observation) */
  optionsCount: number;
  /** Callback to load the next page. Must increment page and call loadMoreOptions. */
  onLoadMore: () => void;
}

interface UseInfiniteScrollReturn {
  /** Wire this to `setIsExpanded` on MenuInput (compose with other handlers) */
  handleExpandedChange: (expanded: boolean) => void;
}

// Prevents loading ALL pages when the dropdown viewport is tall enough
// to show every option without scrolling
const MAX_AUTO_LOADS = 2;

const MAX_DISCOVERY_RETRIES = 8;

/**
 * Delay between discovery retries in ms. DS 5.7.1 runs a 220ms open animation
 * that temporarily sets `overflowY: hidden` and `height: 0px` on the dropdown
 * elements, making them invisible to `findDropdownElement()`. Using 50ms
 * intervals ensures we retry past the animation window (8 × 50ms = 400ms).
 */
const DISCOVERY_RETRY_DELAY_MS = 50;

const PREFETCH_MARGIN = '0px 0px 100px 0px';

/**
 * Finds the last `<li role="option">` inside a listbox container.
 * Uses `querySelectorAll` instead of `lastElementChild` because the DS
 * may render non-option children (loading spinner, "no data" message)
 * after the option elements.
 */
const findLastOption = (container: HTMLElement): Element | null => {
  const options = container.querySelectorAll('[role="option"]');

  return options.length > 0 ? options[options.length - 1] : null;
};

/**
 * IntersectionObserver-based infinite scroll for MenuInput dropdowns.
 *
 * Uses a MutationObserver on the component wrapper's `aria-expanded` attribute
 * to detect when the dropdown opens/closes. DS 5.7.1 changed its internal
 * open/close code paths so the `setIsExpanded` callback is no longer reliably
 * called. The wrapper's `aria-expanded` attribute is always kept in sync with
 * the internal `isOpen` state, making it a reliable signal.
 *
 * When the dropdown opens, discovers the scrollable container via
 * `findDropdownElement`, creates an IntersectionObserver on the last
 * `[role="option"]` element, and fires `onLoadMore` when it enters the viewport.
 */
export const useInfiniteScroll = ({
  dataTestId,
  hasMoreOptions,
  isLoading,
  isLoadingMore,
  optionsCount,
  onLoadMore,
}: UseInfiniteScrollProps): UseInfiniteScrollReturn => {
  const containerRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastObservedRef = useRef<Element | null>(null);
  const autoLoadCountRef = useRef(0);
  const timerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isExpandedRef = useRef(false);
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  // Refs for latest values so the IO callback never reads stale closures
  const stateRef = useRef({ hasMoreOptions, isLoading, isLoadingMore });
  const onLoadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    stateRef.current = { hasMoreOptions, isLoading, isLoadingMore };
  }, [hasMoreOptions, isLoading, isLoadingMore]);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  // ── Observe / re-observe the last option element ──────────────────────
  const observeLastOption = useCallback(() => {
    const container = containerRef.current;

    if (!container || !observerRef.current) {
      return;
    }

    // Stop observing the previous target
    if (lastObservedRef.current) {
      observerRef.current.unobserve(lastObservedRef.current);
      lastObservedRef.current = null;
    }

    const lastOption = findLastOption(container);

    if (lastOption) {
      observerRef.current.observe(lastOption);
      lastObservedRef.current = lastOption;
    }
  }, []);

  // ── Create the IntersectionObserver ───────────────────────────────────
  const createObserver = useCallback((container: HTMLElement) => {
    // Disconnect any previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
      lastObservedRef.current = null;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        const {
          hasMoreOptions: hasMore,
          isLoading: loading,
          isLoadingMore: loadingMore,
        } = stateRef.current;

        if (!hasMore || loading || loadingMore) {
          return;
        }

        if (autoLoadCountRef.current >= MAX_AUTO_LOADS) {
          return;
        }

        autoLoadCountRef.current += 1;
        onLoadMoreRef.current();
      },
      {
        root: container,
        rootMargin: PREFETCH_MARGIN,
      }
    );

    observerRef.current = observer;
    containerRef.current = container;
  }, []);

  // ── Timer-based element discovery ─────────────────────────────────
  // DS 5.7.1 runs a 220ms open animation that hides the dropdown elements.
  // We use setTimeout retries (50ms intervals, 8 attempts = 400ms) to span
  // past the animation window.
  const discoverAndObserve = useCallback(() => {
    let attempts = 0;

    const tryFind = () => {
      attempts += 1;

      const container = findDropdownElement(dataTestId);

      if (container) {
        createObserver(container);
        observeLastOption();

        return;
      }

      if (attempts < MAX_DISCOVERY_RETRIES) {
        timerIdRef.current = setTimeout(tryFind, DISCOVERY_RETRY_DELAY_MS);
      }
    };

    // First attempt with a short delay to let the DOM settle
    timerIdRef.current = setTimeout(tryFind, DISCOVERY_RETRY_DELAY_MS);
  }, [dataTestId, createObserver, observeLastOption]);

  // ── Cleanup helper (IntersectionObserver + discovery timers) ──────────
  const cleanupDiscovery = useCallback(() => {
    if (timerIdRef.current !== null) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
      lastObservedRef.current = null;
    }

    containerRef.current = null;
  }, []);

  // ── Handle dropdown open/close ───────────────────────────────────────
  const onDropdownOpen = useCallback(() => {
    isExpandedRef.current = true;
    autoLoadCountRef.current = 0;
    discoverAndObserve();
  }, [discoverAndObserve]);

  const onDropdownClose = useCallback(() => {
    isExpandedRef.current = false;
    cleanupDiscovery();
    autoLoadCountRef.current = 0;
  }, [cleanupDiscovery]);

  // ── Dropdown open/close lifecycle via setIsExpanded callback ──────────
  // Kept for backward compatibility. DS 5.7.1 may not call this reliably,
  // so the MutationObserver below is the primary detection mechanism.
  const handleExpandedChange = useCallback(
    (expanded: boolean) => {
      if (expanded) {
        onDropdownOpen();
      } else {
        onDropdownClose();
      }
    },
    [onDropdownOpen, onDropdownClose]
  );

  // ── MutationObserver on wrapper's aria-expanded attribute ─────────────
  // DS 5.7.1 sets aria-expanded="true"/"false" on the wrapper element
  // (role="combobox") in sync with its internal isOpen state. This is the
  // reliable signal for dropdown open/close, since setIsExpanded is no
  // longer called on all code paths.
  useEffect(() => {
    const selector = `[data-testid="${escapeForCssAttributeSelector(dataTestId)}"]`;
    const wrapper = document.querySelector<HTMLElement>(selector);

    if (!wrapper) {
      return undefined;
    }

    const mo = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i += 1) {
        const mutation = mutations[i];

        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-expanded'
        ) {
          const expanded =
            (mutation.target as HTMLElement).getAttribute('aria-expanded') ===
            'true';

          if (expanded && !isExpandedRef.current) {
            onDropdownOpen();
          } else if (!expanded && isExpandedRef.current) {
            onDropdownClose();
          }
        }
      }
    });

    mo.observe(wrapper, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });
    mutationObserverRef.current = mo;

    // Check initial state in case the dropdown is already open
    if (
      wrapper.getAttribute('aria-expanded') === 'true' &&
      !isExpandedRef.current
    ) {
      onDropdownOpen();
    }

    return () => {
      mo.disconnect();
      mutationObserverRef.current = null;
    };
  }, [dataTestId, onDropdownOpen, onDropdownClose]);

  // ── Re-observe when options change ───────────────────────────────────
  useEffect(() => {
    if (!isExpandedRef.current) {
      return;
    }

    // Reset auto-load counter when options are cleared (new data source)
    if (optionsCount === 0) {
      autoLoadCountRef.current = 0;
    }

    if (containerRef.current && observerRef.current) {
      // Re-observe the new last option after append/replace
      observeLastOption();
    } else if (optionsCount > 0) {
      // Container wasn't found on open (retries exhausted), retry now
      discoverAndObserve();
    }
  }, [optionsCount, observeLastOption, discoverAndObserve]);

  // Reset auto-load counter when a pagination load completes so IntersectionObserver
  // can request the next page again.
  useEffect(() => {
    if (
      !isLoadingMore &&
      isExpandedRef.current &&
      autoLoadCountRef.current > 0
    ) {
      const container = containerRef.current;

      if (!container) {
        return;
      }

      const stillHasMore = stateRef.current.hasMoreOptions;
      const isScrollable = container.scrollHeight > container.clientHeight;

      if (isScrollable || stillHasMore) {
        autoLoadCountRef.current = 0;
      }
    }
  }, [isLoadingMore]);

  // ── Unmount cleanup ──────────────────────────────────────────────────
  useEffect(
    () => () => {
      cleanupDiscovery();

      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
    },
    [cleanupDiscovery]
  );

  return { handleExpandedChange };
};

export default useInfiniteScroll;
